import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ProceedModalComponent } from '@components/proceed-modal/proceed-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatterDocumentsDispatch } from '@state/global/documents/selected-matter-documents.dispatch';
import {
	DOCUMENT_ACTIONS,
	DocumentResult,
	documentActionData,
} from '@state/global/documents/selected-matter-documents.model';
import { SelectedMatterDocumentsState } from '@state/global/documents/selected-matter-documents.state';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
	DropdownResult,
	SelectionGroup,
} from '../../atomic/atoms/dropdown-options/dropdown-options.component';
import {
	InputModalComponent,
	InputModalData,
} from '../../atomic/molecules/input-modal/input-modal.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import tinymce from 'tinymce';
import { TestHooks } from './documents-matter-viewtor.hooks';

export enum DocumentMatterViewtorState {
	VIEWER = 'viewer',
	EDITOR = 'editor',
}

@UntilDestroy()
@Component({
	selector: 'app-document-matter-viewtor',
	templateUrl: './documents-matter-viewtor.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DocumentsMatterViewtorComponent implements OnInit {
	testHooks = TestHooks;
	@SelectSnapshot(SelectedMatterDocumentsState.getViewDocumentId)
	viewingDocumentId!: string;
	@ViewSelectSnapshot(SelectedMatterDocumentsState.getViewingDocumentTitle)
	viewingDocumentTitle!: string;
	@Select(SelectedMatterDocumentsState.getViewingDocumentUpdating)
	documentUpdating$!: Observable<boolean>;

	@Input() showDelete = false;
	@Input() set sourceId(sourceId: string) {
		this.selectedDocumentDispatch.SelectDocumentsBySourceId([sourceId]);
	}
	@Input() confirmationAction: (action: () => void) => void;
	@Input() disabledElements: string[] = [];

	update: ReplaySubject<string> = new ReplaySubject();
	DocumentMatterViewtorState = DocumentMatterViewtorState;

	tabOptions = [
		{ label: 'Viewer', value: DocumentMatterViewtorState.VIEWER },
		{ label: 'Editor', value: DocumentMatterViewtorState.EDITOR },
	];
	@Select(SelectedMatterDocumentsState.documentActionsSelectionGroup)
	documentActions$!: Observable<SelectionGroup[]>;
	@Select(SelectedMatterDocumentsState.canConvertTags)
	canConvertTags$!: Observable<boolean>;
	public showTagsConvertedWarning$: Observable<boolean>;

	currentState = DocumentMatterViewtorState.VIEWER;

	isViewerReady = false;

	constructor(
		private selectedDocumentDispatch: SelectedMatterDocumentsDispatch,
		private dialog: MatDialog,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.showTagsConvertedWarning$ = this.canConvertTags$.pipe(
			untilDestroyed(this),
			map((x) => !x),
		);
	}

	onActionSelect(evt: DropdownResult<DocumentResult>): void {
		const action = evt?.result?.value;
		const { viewingDocumentId } = this;
		if (action && viewingDocumentId) {
			if (action === DOCUMENT_ACTIONS.RENAME) {
				this.renameDocument(viewingDocumentId);
			} else {
				this.dispatchDocumentTagsAction(action);
			}
		}
	}

	dispatchDocumentTagsAction(
		action:
			| DOCUMENT_ACTIONS.CONVERT
			| DOCUMENT_ACTIONS.REVERT
			| DOCUMENT_ACTIONS.RESET,
	) {
		const { viewingDocumentId } = this;

		this.askForPermission(action).subscribe(() => {
			switch (action) {
				case DOCUMENT_ACTIONS.CONVERT:
					this.analytics.logEvent(AnalyticsEvent.DOCUMENT_CONVERT_TAGS_CLICK, {
						documentId: viewingDocumentId,
					});
					this.selectedDocumentDispatch.ConvertTags(viewingDocumentId);
					tinymce.activeEditor.undoManager.clear();
					break;
				case DOCUMENT_ACTIONS.RESET:
					this.analytics.logEvent(
						AnalyticsEvent.DOCUMENT_RESET_DOCUMENT_CLICK,
						{ documentId: viewingDocumentId },
					);
					this.selectedDocumentDispatch.ResetDocument(viewingDocumentId);
					tinymce.activeEditor.undoManager.clear();
					break;
				case DOCUMENT_ACTIONS.REVERT:
					this.analytics.logEvent(AnalyticsEvent.DOCUMENT_REVERT_TAGS_CLICK, {
						documentId: viewingDocumentId,
					});
					this.selectedDocumentDispatch.RevertTags(viewingDocumentId);
					tinymce.activeEditor.undoManager.clear();
					break;
			}
		});
	}

	private askForPermission(convertType: DOCUMENT_ACTIONS): Observable<void> {
		return this.dialog
			.open(ProceedModalComponent, {
				data: documentActionData[convertType],
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((response) => !!response),
			);
	}

	private renameDocument(documentId: string): void {
		if (documentId) {
			const data: InputModalData<string> = {
				heading: 'Edit document title',
				value: this.viewingDocumentTitle,
			};
			this.dialog
				.open(InputModalComponent, { data })
				.afterClosed()
				.pipe(
					untilDestroyed(this),
					take(1),
					filter((value) => value !== false),
				)
				.subscribe((title) => {
					this.selectedDocumentDispatch.SetDocumentTitle(documentId, title);
				});
		}
	}

	resetDocument() {
		this.dispatchDocumentTagsAction(DOCUMENT_ACTIONS.RESET);
	}
}
